import { diff } from 'deep-object-diff';
import { type ClientFormData, SECTION_FIELDS } from './formDataUtils';
import type { UserConfigurationState } from '@/types/userConfiguration';
import type { RateMultiplier } from '@/types';

// Maps client-side snake_case fields to user-default camelCase fields
const FIELD_MAP: Record<string, keyof UserConfigurationState | { key: keyof UserConfigurationState, nestedKey: string }> = {
  minimum_billable_hours: { key: 'billingStructureConfig', nestedKey: 'minimumBillableHours' },
  half_day_hours: { key: 'billingStructureConfig', nestedKey: 'halfDayHours' },
  week_start: { key: 'billingStructureConfig', nestedKey: 'weekStart' },
  per_week_day_rate_calculation_basis: { key: 'billingStructureConfig', nestedKey: 'perWeekDayRateCalculationBasis' },
  per_diem_base_rate: 'perDiemBaseRate',
  travel_day_rate_multiplier: 'travelDayRateMultiplier',
  multiplier_applies_to_overages: 'multiplierAppliesToOverages',
  uses_gsa_rate: 'usesGsaRate',
  payment_terms_days: 'paymentTermsDays',
  use_late_fee: 'useLateFee',
  late_fee_type: 'lateFeeType',
  late_fee_percentage: 'lateFeePercentage',
  late_fee_fixed_amount: 'lateFeeFixedAmount',
  recurring_late_fee: 'recurringLateFee',
  recurring_late_fee_interval: 'recurringLateFeeInterval',
  use_early_payment_discount: 'useEarlyPaymentDiscount',
  early_payment_type: 'earlyPaymentType',
  early_payment_discount_percentage: 'earlyPaymentDiscountPercentage',
  early_payment_fixed_amount: 'earlyPaymentFixedAmount',
  early_payment_days: 'earlyPaymentDays',
  use_cancellation_policy: 'useCancellationPolicy',
  cancellation_policy: 'cancellationPolicy',
  rush_premium_policy: 'rushPremiumPolicy',
  auto_send_reminders: 'autoSendReminders',
  reminder_days: 'reminderDays',
  employment_type: 'employmentType',
  roles: 'roles',
  rate_multipliers: 'rateMultipliers',
  catalog_holiday_ids: 'holidayCatalogIds',
  custom_holiday_ids: 'holidayCustomIds',
  uses_short_turn: 'usesShortTurn',
  required_break_hours: { key: 'shortTurnConfig', nestedKey: 'requiredBreakHours' },
  short_turn_surcharge_type: { key: 'shortTurnConfig', nestedKey: 'surchargeType' },
  short_turn_fixed_surcharge_amount: { key: 'shortTurnConfig', nestedKey: 'shortTurnFixedSurchargeAmount' },
  short_turn_overtime_offset: { key: 'shortTurnConfig', nestedKey: 'overtimeOffset' },
  short_turn_floor_source: { key: 'shortTurnConfig', nestedKey: 'floorSource' },
  short_turn_floor_tier_code: { key: 'shortTurnConfig', nestedKey: 'floorTierCode' },
  short_turn_floor_tier_custom_value: { key: 'shortTurnConfig', nestedKey: 'floorTierCustomValue' },
  short_turn_premium_basis: { key: 'shortTurnConfig', nestedKey: 'premiumBasis' },
  surcharge_applies_to_shorted_hours_only: { key: 'shortTurnConfig', nestedKey: 'surchargeAppliesToShortedHoursOnly' },
  short_turn_apply_on_half_days: { key: 'shortTurnConfig', nestedKey: 'applyOnHalfDays' },
  apply_holiday_premium: 'applyHolidayPremium',
  holiday_premium_type: 'holidayPremiumType',
  holiday_overtime_offset: 'holidayOvertimeOffset',
  holiday_floor_source: 'holidayFloorSource',
  holiday_floor_tier_code: 'holidayFloorTierCode',
  holiday_floor_tier_custom_value: 'holidayFloorTierCustomValue',
  holiday_fixed_premium_amount: 'holidayFixedPremiumAmount',
  holiday_premium_basis: 'holidayPremiumBasis',
  holiday_apply_on_half_days: 'holidayApplyOnHalfDays',
  surcharge_concurrency_strategy: 'surchargeConcurrencyStrategy',
  // Meal Break
  apply_meal_surcharge: { key: 'mealConfig', nestedKey: 'applyMealSurcharge' },
  meal_deadline_hours: { key: 'mealConfig', nestedKey: 'mealDeadlineHours' },
  recurring_meal_deadline: { key: 'mealConfig', nestedKey: 'recurringMealDeadline' },
  grace_period_minutes: { key: 'mealConfig', nestedKey: 'gracePeriodMinutes' },
  meal_surcharge_type: { key: 'mealConfig', nestedKey: 'mealSurchargeType' },
  use_additional_offset: { key: 'mealConfig', nestedKey: 'useAdditionalOffset' },
  credit_interval_minutes: { key: 'mealConfig', nestedKey: 'creditIntervalMinutes' },
  time_credit_minutes: { key: 'mealConfig', nestedKey: 'timeCreditMinutes' },
  step_up_after_minutes: { key: 'mealConfig', nestedKey: 'stepUpAfterMinutes' },
  meal_premium_basis: { key: 'mealConfig', nestedKey: 'premiumBasis' },
  meal_premium_stacking: { key: 'mealConfig', nestedKey: 'premiumStacking' },
  meal_apply_on_half_days: { key: 'mealConfig', nestedKey: 'applyOnHalfDays' },
  fixed_fee_schedule: { key: 'mealConfig', nestedKey: 'fixedFeeSchedule' },
  meal_floor_source: { key: 'mealConfig', nestedKey: 'floorSource' },
  meal_overtime_offset: { key: 'mealConfig', nestedKey: 'overtimeOffset' },
  meal_floor_tier_code: { key: 'mealConfig', nestedKey: 'floorTierCode' },
  meal_floor_tier_custom_value: { key: 'mealConfig', nestedKey: 'floorTierCustomValue' },
  meal_extended_overtime_enabled: { key: 'mealConfig', nestedKey: 'extendedOvertimeEnabled' },
  meal_extended_overtime_tiers: { key: 'mealConfig', nestedKey: 'extendedOvertimeTiers' },
  meal_trigger_mode: { key: 'mealConfig', nestedKey: 'mealTriggerMode' },
  required_breaks: { key: 'mealConfig', nestedKey: 'requiredBreaks' },
  threshold_hours: { key: 'mealConfig', nestedKey: 'thresholdHours' },
  additional_break_interval_hours: { key: 'mealConfig', nestedKey: 'additionalBreakIntervalHours' },
  required_breaks_half_day: { key: 'mealConfig', nestedKey: 'requiredBreaksHalfDay' },
  // Break settings (from mealConfig)
  meal_break_length_minutes: { key: 'mealConfig', nestedKey: 'mealBreakLengthMinutes' },
  break_billing_policy: { key: 'mealConfig', nestedKey: 'breakBillingPolicy' },
  // Overnight
  apply_overnight_premium: { key: 'overnightConfig', nestedKey: 'applyOvernightPremium' },
  overnight_start_time: { key: 'overnightConfig', nestedKey: 'startTime' },
  overnight_end_time: { key: 'overnightConfig', nestedKey: 'endTime' },
  overnight_premium_type: { key: 'overnightConfig', nestedKey: 'premiumType' },
  overnight_threshold_enabled: { key: 'overnightConfig', nestedKey: 'thresholdEnabled' },
  overnight_threshold_type: { key: 'overnightConfig', nestedKey: 'thresholdType' },
  overnight_percentage_threshold: { key: 'overnightConfig', nestedKey: 'percentageThreshold' },
  overnight_hours_threshold: { key: 'overnightConfig', nestedKey: 'hoursThreshold' },
  overnight_hourly_premium_type: { key: 'overnightConfig', nestedKey: 'premiumType' },
  overnight_hourly_premium_value: { key: 'overnightConfig', nestedKey: 'premiumValue' },
  overnight_hourly_premium_basis: { key: 'overnightConfig', nestedKey: 'premiumBasis' },
  overnight_flat_rate_amount: { key: 'overnightConfig', nestedKey: 'flatRateAmount' },
  // Expenses                                                                                                                    
  expense_policies: { key: 'expenseConfig', nestedKey: 'expensePolicies' },                                                      
  expense_mileage_rate: { key: 'expenseConfig', nestedKey: 'expenseMileageRate' },                                               
  expense_mileage_unit: { key: 'expenseConfig', nestedKey: 'expenseMileageUnit' },                                               
  default_expense_markup_type: { key: 'expenseConfig', nestedKey: 'defaultExpenseMarkupType' },                                  
  default_expense_markup_percentage: { key: 'expenseConfig', nestedKey: 'defaultExpenseMarkupPercentage' },                      
  default_expense_markup_fixed_amount: { key: 'expenseConfig', nestedKey: 'defaultExpenseMarkupFixedAmount' },                   
  expense_receipt_required_above_amount: { key: 'expenseConfig', nestedKey: 'expenseReceiptRequiredAboveAmount' },               
  overnight_apply_on_half_days: { key: 'overnightConfig', nestedKey: 'applyOnHalfDays' },
  overnight_floor_source: { key: 'overnightConfig', nestedKey: 'floorSource' },
  overnight_overtime_offset: { key: 'overnightConfig', nestedKey: 'overtimeOffset' },
  overnight_floor_tier_code: { key: 'overnightConfig', nestedKey: 'floorTierCode' },
  overnight_floor_tier_custom_value: { key: 'overnightConfig', nestedKey: 'floorTierCustomValue' },
  // Gear
  gear_items: { key: 'gearConfig', nestedKey: 'gearItems' },
  gear_kits: { key: 'gearConfig', nestedKey: 'gearKits' },
  cartage: { key: 'gearConfig', nestedKey: 'cartage' },
  gear_week_length_days: { key: 'gearConfig', nestedKey: 'weekLengthDays' },
};

function buildComparisonObjects(fields: string[], clientData: ClientFormData, userDefaults: UserConfigurationState) {
  const clientObj: Record<string, unknown> = {};
  const defaultObj: Record<string, unknown> = {};

  const stripMeta = (arr: Record<string, unknown>[]) => (arr || []).map(
    (item) => ({ ...item, id: undefined, user_id: undefined, created_at: undefined, updated_at: undefined })
  );

  for (const field of fields) {
    const originalClientValue = clientData[field as keyof ClientFormData];
    clientObj[field] = originalClientValue;

    const mapping = FIELD_MAP[field];
    let originalDefaultValue: unknown;

    if (typeof mapping === 'object') { // Nested config like shortTurnConfig
      const configObject = userDefaults[mapping.key] as Record<string, unknown> | undefined;
      // Provide a default empty object for nested configs if they're null/undefined
      originalDefaultValue = (configObject ?? {})[mapping.nestedKey];
      defaultObj[field] = originalDefaultValue;
    } else if (mapping) {
      originalDefaultValue = userDefaults[mapping as keyof UserConfigurationState];
      defaultObj[field] = originalDefaultValue;
    } else {
      originalDefaultValue = undefined;
      defaultObj[field] = undefined;
    }
        
    // Normalize arrays of objects for comparison
    if (['roles', 'rate_multipliers'].includes(field)) {
      const normalizedClient = stripMeta(clientObj[field] as Record<string, unknown>[]);
      const normalizedDefault = stripMeta(defaultObj[field] as Record<string, unknown>[]);
      clientObj[field] = normalizedClient;
      defaultObj[field] = normalizedDefault;
      
    } else if (field === 'catalog_holiday_ids' || field === 'custom_holiday_ids') {
      clientObj[field] = [...((clientObj[field] || []) as string[])].sort();
      defaultObj[field] = [...((defaultObj[field] || []) as string[])].sort();
    }
  }
    
  return { clientObj, defaultObj };
}

export const detectGranularCustomizedFields = (
  clientData: ClientFormData,
  userDefaults: UserConfigurationState
): Record<keyof ClientFormData, boolean> => {

  const customizedFields: Record<string, boolean> = {};
  const allFields = Array.from(new Set(Object.values(SECTION_FIELDS).flat()));
  const { clientObj, defaultObj } = buildComparisonObjects(allFields, clientData, userDefaults);

  const customizedFieldsList: string[] = [];
  for (const field of allFields) {
    const key = field as keyof ClientFormData;
    let clientValue = clientObj[key];
    let defaultValue = defaultObj[key];

    if (clientValue === undefined) clientValue = null;
    if (defaultValue === undefined) defaultValue = null;

    let isCustomized = JSON.stringify(clientValue) !== JSON.stringify(defaultValue);

    // If a feature is turned off for both the user default and the client,
    // then none of its sub-fields should be considered "customized", even if their
    // underlying values differ (e.g., null vs undefined, or 0 vs null).
    const fieldKey = field as keyof ClientFormData;
    const isShortTurnField = SECTION_FIELDS.shortTurn.includes(fieldKey) && field !== 'uses_short_turn';
    const isHolidayField = SECTION_FIELDS.holiday.includes(fieldKey) && field !== 'apply_holiday_premium';
    const isMealBreakField = SECTION_FIELDS.mealBreak.includes(fieldKey) && field !== 'apply_meal_surcharge';
    const isOvernightField = SECTION_FIELDS.overnight.includes(fieldKey) && field !== 'apply_overnight_premium';
    
    if (isShortTurnField && clientObj['uses_short_turn'] === false && defaultObj['uses_short_turn'] === false) {
      isCustomized = false;
    }
    if (isHolidayField && clientObj['apply_holiday_premium'] === false && defaultObj['apply_holiday_premium'] === false) {
      isCustomized = false;
    }
    if (isMealBreakField && clientObj['apply_meal_surcharge'] === false && defaultObj['apply_meal_surcharge'] === false) {
      isCustomized = false;
    }
    if (isOvernightField && clientObj['apply_overnight_premium'] === false && defaultObj['apply_overnight_premium'] === false) {
      isCustomized = false;
    }

    // Special case to prevent false positive customization on surcharge method
    if (field === 'short_turn_surcharge_type' && isCustomized) {
      const clientUsesShortTurn = clientObj['uses_short_turn'];
      const defaultUsesShortTurn = defaultObj['uses_short_turn'];

      // If short turn was just enabled (default is false, client is true)
      if (clientUsesShortTurn === true && defaultUsesShortTurn === false) {
        // Check if the surcharge method is the one that would be set automatically
        const hasExtraMultipliers = ((clientObj['rate_multipliers'] ?? []) as RateMultiplier[]).some(m => m.code !== "STRAIGHT_TIME");
        const implicitDefault = hasExtraMultipliers ? "carry_over" : "fixed_dollar";
        
        if (clientValue === implicitDefault) {
          isCustomized = false; // It's not a manual customization, so override.
        }
      }
    }

    // Special case for the violation only toggle
    if (field === 'surcharge_applies_to_shorted_hours_only' && isCustomized) {
      const clientUsesShortTurn = clientObj['uses_short_turn'];
      const defaultUsesShortTurn = defaultObj['uses_short_turn'];

      // If short turn was just enabled (default is false, client is true)
      // and the client value is the implicit default of 'false'
      if (clientUsesShortTurn === true && defaultUsesShortTurn === false && clientValue === false) {
          isCustomized = false; // It's not a manual customization, so override.
      }
    }

    customizedFields[key] = isCustomized;

    if (isCustomized) {
      customizedFieldsList.push(field);
    }
  }

  return customizedFields as Record<keyof ClientFormData, boolean>;
};

export const detectCustomizedSections = (
  clientData: ClientFormData,
  userDefaults: UserConfigurationState
): { billing: boolean; breaks: boolean; invoicing: boolean; roles: boolean; multipliers: boolean; shortTurn: boolean; holiday: boolean; mealBreak: boolean; overnight: boolean; expenses: boolean; gear: boolean; } => {

  const isCustom = (sectionName: keyof typeof SECTION_FIELDS): boolean => {
    const fields = SECTION_FIELDS[sectionName];
    
    // Special handling for sections that depend on a toggle
    if (sectionName === 'shortTurn' && clientData.uses_short_turn !== userDefaults.usesShortTurn) return true;
    if (sectionName === 'shortTurn' && !clientData.uses_short_turn) return false;
    if (sectionName === 'holiday' && clientData.apply_holiday_premium !== userDefaults.applyHolidayPremium) return true;
    if (sectionName === 'holiday' && !clientData.apply_holiday_premium) return false;
    if (sectionName === 'mealBreak' && clientData.apply_meal_surcharge !== userDefaults.mealConfig?.applyMealSurcharge) return true;
    if (sectionName === 'mealBreak' && !clientData.apply_meal_surcharge) return false;
    if (sectionName === 'overnight' && clientData.apply_overnight_premium !== userDefaults.overnightConfig?.applyOvernightPremium) return true;
    if (sectionName === 'overnight' && !clientData.apply_overnight_premium) return false;

    const { clientObj, defaultObj } = buildComparisonObjects(fields, clientData, userDefaults);
    
    // For arrays, stringify for a simple, effective comparison. For objects, diff works well.
    if (Array.isArray(clientObj[fields[0]])) {
        return JSON.stringify(clientObj) !== JSON.stringify(defaultObj);
    }
    return Object.keys(diff(defaultObj, clientObj)).length > 0;
  };

  return {
    billing: isCustom('dayRate') || isCustom('travel'),
    breaks: isCustom('breaks'),
    invoicing: isCustom('invoicing'),
    roles: isCustom('roles'),
    multipliers: isCustom('multipliers'),
    shortTurn: isCustom('shortTurn'),
    holiday: isCustom('holiday'),
    mealBreak: isCustom('mealBreak'),
    overnight: isCustom('overnight'),
    expenses: isCustom('expenses'),
    gear: isCustom('gear'),
  };
};
